import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { MEDIA_QUERIES } from 'constants'

import { unixToFormattedLocalTime, unixToFormattedLocalDate } from 'react-wot-bootstrap/lib/helpers/datetime'

import {
    Processing,
    ClanTag,
    ScrollArea,
    Tooltip,
    Table,
    TableRow,
    TableHead,
} from 'react-wot-bootstrap'
import TextOverflow from 'components/TextOverflow/TextOverflow'

import { battleInfoPropTypes } from '../../propTypes'

import {
    BEGIN,
    SERVER,
    PROVINCE,
    MAP,
    BASE,
    ENEMY,
    ENEMY_NOT_DEFINED,
    NO_CLAN_BATTLES,
} from './translations'

import styles from './BattleLog.scss'

export class BattleLog extends React.Component {
    static propTypes = {
        isInitialLoading: PropTypes.bool.isRequired,
        battleSchedule: PropTypes.arrayOf(battleInfoPropTypes),
    }

    constructor(props) {
        super(props)

        this.state = {
            scrollAreaHeight: 0,
            isSmallMqActive: window.matchMedia(MEDIA_QUERIES.EXTRA_SMALL_WIDTH).matches,
        }

        this.BOTTOM_SCROLL_OFFSET = 10
        this.SCROLL_STEP = 40
    }

    componentDidMount() {
        window.matchMedia(MEDIA_QUERIES.EXTRA_SMALL_WIDTH).addEventListener('change', this.handleMediaQueryChange)
        window.addEventListener('resize', this.setScrollAreaHeight)
        this.setScrollAreaHeight()
    }

    componentWillUnmount() {
        window.matchMedia(MEDIA_QUERIES.EXTRA_SMALL_WIDTH).removeEventListener('change', this.handleMediaQueryChange)
        window.removeEventListener('resize', this.setScrollAreaHeight)
    }

    componentDidUpdate(prevProps) {
        if (this.props.battleSchedule && prevProps.battleSchedule.length !== this.props.battleSchedule.length) {
            this.setScrollAreaHeight()
        }
    }

    setScrollAreaHeight = () => {
        if (!this._table || !this._tableHead) return

        const tableHeadHeight = this._tableHead.clientHeight
        const coords = this._table.getBoundingClientRect()
        const scrollAreaHeight = window.innerHeight - coords.top - tableHeadHeight - this.BOTTOM_SCROLL_OFFSET
        if (this.state.scrollAreaHeight !== scrollAreaHeight) {
            this.setState({
                scrollAreaHeight,
            })
        }
    }

    handleMediaQueryChange = (event) => {
        const isSmallMqActive = event.matches

        if (this.state.isSmallMqActive !== isSmallMqActive) {
            this.setState({ isSmallMqActive })
        }
    }

    renderBattleInfoItem = (battleInfo) => {
        const {
            provinceName,
            arenaName,
            peripheryName,
            respawnName,
            isPlanned,
            enemyClan,
            startsAt,
            provinceId,
        } = battleInfo

        const { isSmallMqActive } = this.state

        const clanNameTooltipId = `tooltip-clan-name-${startsAt}`
        const clanNameTooltipContent = (enemyClan && (
            <div>
                <ClanTag
                    size="medium"
                    tag={enemyClan.tag}
                    color={enemyClan.color}
                />
                <span className={styles.clanName}>
                    {enemyClan.name}
                </span>
            </div>
        ))

        const planedClassNames = classNames(styles.planned, {
            [styles.planned__visible]: isPlanned,
        })

        return (
            <TableRow key={`${startsAt} + ${provinceId}`}>
                <div className={classNames(styles.td, styles.td__begin)}>
                    <div className={styles.dateContainer}>
                        <div className={styles.time}>
                            {unixToFormattedLocalTime(startsAt)}
                            <span className={planedClassNames}>
                                &thinsp;
                                {'+'}
                            </span>
                        </div>
                        <div className={styles.date}>{unixToFormattedLocalDate(startsAt)}</div>
                    </div>
                </div>
                <div className={classNames(styles.td, styles.td__server)}>
                    <TextOverflow tooltipId={`tooltip-periphery-name-${startsAt}`}>
                        {peripheryName}
                    </TextOverflow>
                </div>
                <div className={classNames(styles.td, styles.td__province)}>
                    <TextOverflow tooltipId={`tooltip-province-name-${startsAt}`}>
                        {provinceName}
                    </TextOverflow>
                </div>
                <div className={classNames(styles.td, styles.td__map)}>
                    <TextOverflow tooltipId={`tooltip-arena-name-${startsAt}`}>
                        {arenaName}
                    </TextOverflow>
                </div>
                <div className={classNames(styles.td, styles.td__base)}>
                    {respawnName ? (
                        <TextOverflow tooltipId={`tooltip-respawn-name-${startsAt}`}>
                            {respawnName}
                        </TextOverflow>
                    ) : (
                        <div className={styles.dashes}>{'– –'}</div>
                    )}
                </div>
                <div className={classNames(styles.td, styles.td__enemy)}>
                    {enemyClan ? (
                        <div
                            className={styles.clan}
                            data-tip={clanNameTooltipId}
                            data-for={clanNameTooltipId}
                        >
                            <div className={styles.clanTag}>
                                <ClanTag
                                    size="medium"
                                    tag={enemyClan.tag}
                                    color={enemyClan.color}
                                    emblem={enemyClan.emblemUrl}
                                />
                            </div>
                            {isSmallMqActive ? (
                                <TextOverflow
                                    tooltipId={clanNameTooltipId}
                                    tooltipContent={clanNameTooltipContent}
                                >
                                    {enemyClan.name}
                                </TextOverflow>
                            ) : (
                                <Tooltip
                                    id={clanNameTooltipId}
                                    getContent={() => clanNameTooltipContent}
                                />
                            )}
                        </div>
                    ) : (
                        <TextOverflow
                            classMix={styles.enemyNotDefined}
                            tooltipId={`tooltip-clan-not-defined-${startsAt}`}
                        >
                            {ENEMY_NOT_DEFINED}
                        </TextOverflow>
                    )}
                </div>
            </TableRow>
        )
    }

    renderContent() {
        const { battleSchedule, isInitialLoading } = this.props
        if (isInitialLoading) return (
            <div style={{ height: this.state.scrollAreaHeight }}>
                <Processing
                    type='local'
                    isVisible
                />
            </div>
        )

        if (!battleSchedule.length) return (
            <div
                className={styles.tableInfo}
                style={{ height: this.state.scrollAreaHeight }}
            >
                {NO_CLAN_BATTLES}
            </div>
        )

        return (
            <ScrollArea
                modifier="table"
                stepHeight={this.SCROLL_STEP}
                scrollableAreaHeight={this.state.scrollAreaHeight}
            >
                {battleSchedule.map(this.renderBattleInfoItem)}
            </ScrollArea>
        )
    }

    render() {
        return (
            <div ref={el => this._table = el}>
                <Table isScrollable>
                    <div ref={el => this._tableHead = el}>
                        <TableHead>
                            <div className={classNames(styles.th, styles.th__begin)}>{BEGIN}</div>
                            <div className={classNames(styles.th, styles.th__server)}>{SERVER}</div>
                            <div className={classNames(styles.th, styles.th__province)}>{PROVINCE}</div>
                            <div className={classNames(styles.th, styles.th__map)}>{MAP}</div>
                            <div className={classNames(styles.th, styles.th__base)}>{BASE}</div>
                            <div className={classNames(styles.th, styles.th__enemy)}>{ENEMY}</div>
                        </TableHead>
                    </div>
                    {this.renderContent()}
                </Table>
            </div>
        )
    }
}

export default BattleLog



// WEBPACK FOOTER //
// ./containers/WGCWXBattleSchedule/components/BattleLog/BattleLog.jsx